import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material'
import { createContext, useEffect, useState } from 'react'
import { logEvent } from '../analyticsService'
import { withdrawRewardPayment } from '../services/blockchainService'
import {
    adjudicateClaim,
    deleteAdjudicationLogs,
    getBlockchainPaymentByClaimNumber,
    getIsPayer,
    postClaim,
} from '../services/claimsService'
import { getConfig } from '../services/miscService'
import { AdjudicateClaimResponse } from '../services/models/adjudication'
import { ClaimRequest } from '../services/models/claims'
import AdjudicateClaimResultsDialog from './dialogs/adjudicate-claim/AdjudicateClaimResultsDialog'
import ErrorDialog, { formatError } from './dialogs/ErrorDialog'

interface AdjucateClaimProps {
    claimNumber: string
    data: {
        claim: ClaimRequest
        status: string
    }
    onUpdate: () => void
}

export const AdjudicationDataContext = createContext({
    errorData: undefined,
    errorDialogOpen: false,
    closeErrorDialog: () => {},
} as { errorData?: string; errorDialogOpen: boolean; closeErrorDialog: () => void })

const AdjudicateClaim = ({ claimNumber, data, onUpdate }: AdjucateClaimProps) => {
    const [enabled, setEnabled] = useState(false)
    const [loading, setLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [submitEnabled, setSubmitEnabled] = useState(true)
    const [adjudicationData, setAdjudicationData] = useState<AdjudicateClaimResponse>()

    const [paymentWarningOpen, setPaymentWarningOpen] = useState(false)
    const [ediData, setEdiData] = useState<any>()

    const [errorData, setErrorData] = useState<any>()
    const [errorDialogOpen, setErrorDialogOpen] = useState(false)

    useEffect(() => {
        const resolveIsEnabled = async () => {
            const {
                data: { isPayer },
            } = await getIsPayer(claimNumber)

            if (isPayer && data.status === 'Submitted') {
                setEnabled(true)
            }
        }
        resolveIsEnabled()
    }, [])

    const onAdjudicateClaim = async () => {
        setEnabled(false)
        setLoading(true)
        logEvent('adjudicate_button_click', { appName: window.location.host })
        const { data: results } = await adjudicateClaim(claimNumber, false)
        setAdjudicationData(results)
        setLoading(false)
        setDialogOpen(true)
        onUpdate()
    }

    const onSubmitClaim = async () => {
        setSubmitEnabled(false)
        logEvent('adjudicate_submit', {
            appName: window.location.host,
        })
        const _ediData = new FormData()
        _ediData.append('files', new Blob([adjudicationData?.edi835 ?? '']))
        setEdiData(_ediData)
        // TODO: Catch this if failure?
        setDialogOpen(false)
        setLoading(true)
        // withdraw payment
        try {
            const config = await getConfig()
            const _paymentId =
                data.claim?.privateData?.paymentId ??
                (await getBlockchainPaymentByClaimNumber(data.claim?.publicData?.claimNumber)).data
                    .id
            if (!_paymentId) {
                setPaymentWarningOpen(true)
            } else {
                if (config.data.blockchainPaymentsEnabled) await withdrawRewardPayment(_paymentId)
                await postClaim(_ediData)
                setLoading(false)
                onUpdate()
            }
        } catch (err) {
            setErrorData(formatError(err))
            setErrorDialogOpen(true)
        }
    }

    const onClearProcessedClaim = () => {
        logEvent('adjudicate_cancel', {
            appName: window.location.host,
        })
        // TODO: Catch this if failure?
        deleteAdjudicationLogs(claimNumber)
        setDialogOpen(false)
        setEnabled(true)
        onUpdate()
    }

    const renderResultsDialog = () => {
        if (adjudicationData && Object.keys(adjudicationData).length) {
            return (
                <AdjudicateClaimResultsDialog
                    adjudicatedClaim={adjudicationData}
                    open={dialogOpen}
                    onSubmit={onSubmitClaim}
                    onClose={() => {
                        setDialogOpen(false)
                        setEnabled(true)
                    }}
                    onClearResults={onClearProcessedClaim}
                    disableSubmit={!submitEnabled}
                />
            )
        }
    }

    const renderAdjudicateButton = () => {
        if (loading) {
            return <CircularProgress sx={{ margin: 'auto' }} />
        }
        return (
            <Button
                sx={{
                    maxWidth: '100%',
                    margin: 'auto',
                }}
                disableElevation={true}
                variant="contained"
                onClick={onAdjudicateClaim}
                disabled={!enabled}
            >
                Adjudicate
            </Button>
        )
    }

    return (
        <>
            {renderResultsDialog()}
            {renderAdjudicateButton()}
            <PaymentWarning
                open={paymentWarningOpen}
                onContinue={async () => {
                    try {
                        setPaymentWarningOpen(false)
                        await postClaim(ediData, true)
                        setLoading(false)
                        onUpdate()
                    } catch (err) {
                        setErrorData(formatError(err))
                        setErrorDialogOpen(true)
                    }
                }}
                onClose={() => {
                    setLoading(false)
                    setPaymentWarningOpen(false)
                }}
            />
            <ErrorDialog
                data={errorData}
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
            />
        </>
    )
}

interface PaymentWarningProps {
    open: boolean
    onContinue: () => void
    onClose: () => void
}

const PaymentWarning = ({ open, onContinue, onClose }: PaymentWarningProps) => {
    return (
        <Dialog open={open}>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReportProblemIcon sx={{ marginRight: 1 }} color="warning" />
                    <Typography variant="h6">Cannot withdraw reward</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography>No reward fee will be collected from processing this claim.</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onContinue}>Continue</Button>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    )
}

export default AdjudicateClaim
